import { useEffect, useCallback, useState, useRef } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Typography } from "@pondigitalsolutions/pon-component-library";
import { useParams, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { languageDutch, languageEnglish } from "../i18n";
import { vacanciesOverviewItemsCount } from "../constants";
import { Alert } from "@components/Alert";
import { Layout } from "@components/Layout";
import { VacancyCard } from "@components/VacancyCard";
import { Pagination } from "@components/Pagination";
import { Loader } from "@components/Loader";
import { StatusMessage } from "@components/StatusMessage";
import { useGetVacancies } from "@hooks/useGetVacancies";
import { VacanciesFilter } from "@components/VacanciesFilter";
import { useDebounce } from "@hooks/useDebounce";
import { SearchBar } from "../components/SearchBar";
import { useCurrentLang } from "@hooks/useCurrentLang";

export const VacanciesPage = () => {
  const { lang } = useParams();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentLang = useCurrentLang(lang);

  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const debouncedSearchValue = useDebounce(inputValue, 500);
  const skipNextDebounceApply = useRef(false);

  const pageParam = searchParams.get("p");
  const cityParam = searchParams.get("city");
  const departmentParam = searchParams.get("department");
  const companyParam = searchParams.get("company");
  const regionParam = searchParams.get("region");
  const countryCodeParam = searchParams.get("countryCode");
  const currentPage = pageParam ? Number.parseInt(pageParam, 10) : 1;

  const {
    data: vacanciesData,
    isLoading,
    error,
  } = useGetVacancies({
    per_page: vacanciesOverviewItemsCount,
    page: currentPage,
    locale: currentLang,
    city: cityParam || undefined,
    department: departmentParam || undefined,
    company: companyParam || undefined,
    region: regionParam || undefined,
    search: searchQuery || undefined,
    countryCode: countryCodeParam || undefined,
  });

  const setPage = useCallback(
    (page: number) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("p", page.toString());
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  // Handle search submit
  const handleSearch = (value: string) => {
    // If we are clearing the search, set a guard so the stale debounced value (old query)
    // isn't re-applied in the debounce effect, which causes a brief flash of old results.
    if (value === "") {
      skipNextDebounceApply.current = true;
    }
    setSearchQuery(value);
    setInputValue(value);
    setPage(1); // Reset to first page on new search
  };

  // Update search query based on debounced input
  useEffect(() => {
    if (skipNextDebounceApply.current) {
      // Consume the skip flag once
      skipNextDebounceApply.current = false;
      return;
    }
    if (debouncedSearchValue !== searchQuery) {
      setSearchQuery(debouncedSearchValue);
      setPage(1);
    }
  }, [debouncedSearchValue, searchQuery, setPage]);

  // Scroll to top when page changes or search changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [currentPage, searchQuery]);

  const totalPages = vacanciesData?.meta?.totalCount
    ? Math.ceil(vacanciesData.meta.totalCount / vacanciesOverviewItemsCount)
    : 1;

  // Check if we have no results (could be out of bounds page or just no data)
  const hasNoResults =
    !isLoading &&
    !error &&
    (!vacanciesData?.data || vacanciesData.data.length === 0);

  const hasFilters =
    searchQuery.trim().length > 0 ||
    !!cityParam ||
    !!departmentParam ||
    !!companyParam ||
    !!regionParam;

  const pageSlug = "vacancies";
  const translatedSlug =
    currentLang === languageDutch
      ? `/${languageEnglish}/${pageSlug}`
      : `/${pageSlug}`;

  return (
    <Layout translatedSlug={translatedSlug}>
      <title>{t("Vacancies.title")}</title>

      <section className="bg-secondary-900 pb-20 md:pb-48 dark:bg-transparent">
        <div className="mx-auto max-w-360 px-4 py-14 md:px-4 2xl:px-0">
          <div className="flex flex-col items-center gap-6">
            <div className="flex w-full justify-center">
              <Typography as="h1" size="3xl" className="text-white md:text-4xl">
                {t("Vacancies.heading")}
              </Typography>
              {!isLoading && !error && vacanciesData?.meta?.totalCount && (
                <Typography
                  as="span"
                  className={twMerge(
                    "ml-0.5 flex h-6 items-center justify-center rounded-full border border-primary-500 bg-primary-500 text-sm dark:border-secondary-500 dark:bg-transparent dark:text-secondary-100",
                    vacanciesData.meta.totalCount.toString().length > 2
                      ? "px-3 py-2"
                      : "w-6"
                  )}
                  data-testid="vacancies-count-badge"
                >
                  {vacanciesData.meta.totalCount}
                </Typography>
              )}
            </div>
            <div className="mt-4 w-full max-w-230">
              <SearchBar
                placeholder={t("Vacancies.searchPlaceholder")}
                isLoading={isLoading}
                isDisabled={isLoading}
                onClick={handleSearch}
                onInputChange={(value) => {
                  setInputValue(value);
                }}
                searchValue={inputValue}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="relative -top-22 z-[2] mx-auto w-full max-w-360 px-4 md:-top-24 md:-mt-20 md:px-4 2xl:px-0">
        <div
          className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 lg:grid-cols-3 lg:items-start xl:grid-cols-4"
          data-testid="vacancies-grid"
        >
          <div className="md:col-span-2 lg:col-span-1">
            <VacanciesFilter />
          </div>
          <div
            className="grid scroll-mt-32 gap-6 md:col-span-2 md:grid-cols-2 md:gap-8 lg:col-span-2 lg:grid-cols-2 xl:col-span-3 xl:grid-cols-3"
            id="vacancies-overview"
          >
            {isLoading && (
              <div className="flex h-60 items-center justify-center md:col-span-2 lg:col-span-2 xl:col-span-3">
                <Loader data-testid="vacancies-loading" />
              </div>
            )}

            {error && (
              <div className="flex h-60 items-center justify-center md:col-span-2 lg:col-span-3">
                <Alert
                  variant="error"
                  labelText={t("Vacancies.error.label")}
                  text={t("Vacancies.error.text")}
                  textClassName="text-tertiary-100"
                />
              </div>
            )}

            {hasNoResults && (
              <div className="flex h-60 items-center justify-center md:col-span-2 lg:col-span-1 xl:col-span-2">
                <StatusMessage
                  variant="warning"
                  pillMessage={t("Vacancies.noResults.label")}
                  className="[&>span]:lg:text-tertiary-100"
                  message={
                    <Trans
                      i18nKey={
                        hasFilters
                          ? "Vacancies.noResults.searchText"
                          : "Vacancies.noResults.text"
                      }
                      components={{
                        1: (
                          <button
                            type="button"
                            onClick={() => {
                              if (hasFilters) {
                                handleSearch("");
                                searchParams.delete("city");
                                searchParams.delete("department");
                                searchParams.delete("company");
                                searchParams.delete("region");
                                setSearchParams(searchParams);
                              } else {
                                setPage(1);
                              }
                            }}
                            aria-label={
                              hasFilters
                                ? t("Vacancies.clearSearch")
                                : t("Vacancies.goToFirstPage")
                            }
                            className="text-sm font-semibold underline hover:no-underline lg:text-tertiary-100"
                          >
                            {hasFilters
                              ? t("Vacancies.clearSearch")
                              : t("Vacancies.goToFirstPage")}
                          </button>
                        ),
                      }}
                    />
                  }
                />
              </div>
            )}

            {!isLoading &&
              !error &&
              !hasNoResults &&
              vacanciesData?.data?.map((vacancy) => (
                <VacancyCard
                  key={vacancy.id}
                  vacancy={vacancy}
                  data-vacancy-card={false}
                />
              ))}
          </div>
        </div>

        {/* Pagination */}
        {!isLoading && !error && vacanciesData?.data && totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            setPage={setPage}
            pathname={globalThis.location.pathname}
          />
        )}
      </section>
    </Layout>
  );
};
